import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { DROP_REACH, snapSquare } from './snapSquare';

/**
 * Dragging a pawn or a card with a mouse, pen or finger (pointer events, so touch works too).
 * A press only becomes a drag once it moves a few pixels: until then it is an ordinary tap or
 * click. While dragging, a copy of the dragged thing follows the pointer (lifted above a finger,
 * so the finger does not hide it) and the square under it is reported, a near miss of a lit
 * square counting as that square; on release the drop is reported and the click the release would
 * cause is swallowed.
 */

export type DragSource =
  | { readonly kind: 'pawn'; readonly pawnId: string }
  | { readonly kind: 'card'; readonly cardId: string };

export interface ActiveDrag {
  readonly source: DragSource;
  /** Size of the dragged element, to draw its copy at the same size. */
  readonly width: number;
  readonly height: number;
}

export interface DragHandlers {
  /** The press has moved far enough: it is a drag. */
  readonly onStart: (source: DragSource) => void;
  /** The square under the dragged thing changed (its data-square name), or null off the board. */
  readonly onOver: (square: string | null) => void;
  /** Released over a square, or null (off the board, or cancelled). */
  readonly onDrop: (source: DragSource, square: string | null) => void;
}

/** How far a press must move to become a drag, in CSS pixels. */
const THRESHOLD: Readonly<Record<string, number>> = { mouse: 5, pen: 6, touch: 8 };

/** How far above a finger the dragged copy floats, as a share of its height. */
const TOUCH_LIFT = 0.6;

/**
 * Swallows the click a drag's release causes (a drag that ends where it began would otherwise
 * also tap what it ended on). Only that click: one at the release point, straight after it.
 */
export function swallowReleaseClick(x: number, y: number): void {
  const at = performance.now();
  const stop = (e: MouseEvent) => {
    window.removeEventListener('click', stop, true);
    if (performance.now() - at > 500 || Math.hypot(e.clientX - x, e.clientY - y) > 12) return;
    e.stopPropagation();
    e.preventDefault();
  };
  window.addEventListener('click', stop, true);
  // A drag that ends away from where it started causes no click: stop waiting for one.
  setTimeout(() => window.removeEventListener('click', stop, true), 500);
}

export function useDragGesture(handlers: DragHandlers): {
  readonly active: ActiveDrag | null;
  /** Call from a pointerdown on a draggable element. */
  readonly begin: (source: DragSource, e: ReactPointerEvent, element: Element) => void;
  /** Lets go of a press that has not become a drag (it became a long press, say). */
  readonly cancel: () => void;
  /** Ref for the dragged copy: it is positioned under the pointer. */
  readonly ghostRef: (el: HTMLElement | null) => void;
} {
  const latest = useRef(handlers);
  useLayoutEffect(() => {
    latest.current = handlers;
  });
  const [active, setActive] = useState<ActiveDrag | null>(null);
  const ghost = useRef<HTMLElement | null>(null);
  const pointer = useRef({ x: 0, y: 0, touch: false });
  const stopListening = useRef<(() => void) | null>(null);

  const place = useCallback(() => {
    const el = ghost.current;
    if (!el) return;
    const { x, y, touch } = pointer.current;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const lift = touch ? h * TOUCH_LIFT : 0;
    el.style.transform = `translate(${x - w / 2}px, ${y - h / 2 - lift}px)`;
  }, []);

  const ghostRef = useCallback(
    (el: HTMLElement | null) => {
      ghost.current = el;
      place();
    },
    [place],
  );

  const begin = useCallback(
    (source: DragSource, e: ReactPointerEvent, element: Element) => {
      if (!e.isPrimary || e.button !== 0) return;
      stopListening.current?.();
      const pointerId = e.pointerId;
      const start = { x: e.clientX, y: e.clientY };
      const rect = element.getBoundingClientRect();
      const touch = e.pointerType === 'touch';
      const limit = THRESHOLD[e.pointerType] ?? 6;
      let dragging = false;
      let over: string | null = null;
      // The square under the middle of the dragged copy (above the finger on touch screens), or
      // under the finger itself; a near miss of a lit square is that square (snapSquare.ts).
      const target = (x: number, y: number) =>
        snapSquare(
          touch
            ? [
                { x, y: y - rect.height * TOUCH_LIFT },
                { x, y },
              ]
            : [{ x, y }],
          DROP_REACH,
        );

      const move = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return;
        pointer.current = { x: ev.clientX, y: ev.clientY, touch };
        if (!dragging) {
          if (Math.hypot(ev.clientX - start.x, ev.clientY - start.y) < limit) return;
          dragging = true;
          document.documentElement.classList.add('is-dragging');
          setActive({ source, width: rect.width, height: rect.height });
          latest.current.onStart(source);
        }
        ev.preventDefault();
        place();
        const sq = target(ev.clientX, ev.clientY);
        if (sq !== over) {
          over = sq;
          latest.current.onOver(sq);
        }
      };
      const end = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return;
        cleanup();
        if (!dragging) return;
        swallowReleaseClick(ev.clientX, ev.clientY);
        latest.current.onDrop(source, ev.type === 'pointercancel' ? null : over);
      };
      const cleanup = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', end);
        window.removeEventListener('pointercancel', end);
        document.documentElement.classList.remove('is-dragging');
        stopListening.current = null;
        setActive(null);
      };
      window.addEventListener('pointermove', move, { passive: false });
      window.addEventListener('pointerup', end);
      window.addEventListener('pointercancel', end);
      stopListening.current = cleanup;
    },
    [place],
  );

  // A drag in progress when the screen goes away is dropped nowhere.
  useEffect(() => () => stopListening.current?.(), []);

  const cancel = useCallback(() => {
    if (!document.documentElement.classList.contains('is-dragging')) stopListening.current?.();
  }, []);

  return { active, begin, cancel, ghostRef };
}
